import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Space,
  Image,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axiosLib from "axios";

// axios instance с baseURL из .env
const axios = axiosLib.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

type Promotion = {
  id: number;
  title: string;
  image_url: string;
  created_at: string;
};

export default function PromotionAdmin() {
  const [data, setData] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [form] = Form.useForm();

  // Загрузка акций
  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/promotion/");
      setData(res.data.promotions || []);
    } catch (e) {
      message.error("Ошибка загрузки акций");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  // Создать/обновить акцию
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append("title", values.title);
      // Берём файл правильно
      if (values.image && values.image.length && values.image[0].originFileObj) {
        formData.append("image", values.image[0].originFileObj);
      }

      if (editing) {
        // Обновление
        await axios.put(`/promotion/${editing.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success("Акция обновлена");
      } else {
        // Создание
        await axios.post("/promotion/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success("Акция создана");
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      fetchPromotions();
    } catch (e: any) {
      if (e?.response?.data?.error) {
        message.error(e.response.data.error);
      } else {
        message.error("Ошибка сохранения");
      }
    }
  };

  // Открыть модалку редактирования
  const handleEdit = (record: Promotion) => {
    setEditing(record);
    setModalOpen(true);
    form.setFieldsValue({ title: record.title, image: [] }); // image: [] чтобы не было мусора
  };

  // Удалить акцию
  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`/promotion/${id}`);
      message.success("Акция удалена");
      fetchPromotions();
    } catch (e) {
      message.error("Ошибка удаления");
    }
  };

  const columns: ColumnsType<Promotion> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 60,
    },
    {
      title: "Заголовок",
      dataIndex: "title",
    },
    {
      title: "Изображение",
      dataIndex: "image_url",
      render: (url) =>
        url ? <Image src={url} width={80} /> : <span>Нет</span>,
      width: 120,
    },
    {
      title: "Дата",
      dataIndex: "created_at",
      render: (date) => new Date(date).toLocaleString(),
      width: 180,
    },
    {
      title: "Действия",
      dataIndex: "actions",
      width: 130,
      render: (_: any, record: Promotion) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Удалить акцию?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
        onClick={() => {
          setModalOpen(true);
          setEditing(null);
          form.resetFields();
        }}
      >
        Добавить акцию
      </Button>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        open={modalOpen}
        title={editing ? "Редактировать акцию" : "Добавить акцию"}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onOk={handleOk}
        okText={editing ? "Сохранить" : "Добавить"}
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          preserve={false}
        >
          <Form.Item
            label="Заголовок"
            name="title"
            rules={[{ required: true, message: "Введите заголовок" }]}
          >
            <Input placeholder="Введите название акции" />
          </Form.Item>
          <Form.Item
            label="Изображение"
            name="image"
            valuePropName="fileList"
            getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
            rules={[
              {
                required: !editing,
                message: "Загрузите изображение",
              },
            ]}
          >
            <Upload
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
              listType="picture"
              showUploadList={{ showRemoveIcon: true }}
            >
              <Button icon={<UploadOutlined />}>Выбрать файл</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
